import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createShareLink, getReportEmailDraft, getShareLink, revokeShareLink } from '../services/api';
import { apiErrorMessage } from '../utils/apiError';
import ConfirmDialog from './ConfirmDialog';
import SendReportDialog, { sentNotice as noticeAfterSending } from './SendReportDialog';

/**
 * The client link of a report: sent along with the PDF, it opens the report
 * online, where the recipient can enlarge and zoom into the annotated photos.
 * One link per report; deactivating it cuts access at once.
 */
export default function ShareLinkPanel({ reportId }) {
  const { t, i18n } = useTranslation();
  const [link, setLink] = useState(undefined); // undefined = loading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [draft, setDraft] = useState(null); // e-mail: enabled?, suggestions, history
  const [sending, setSending] = useState(false);
  const [sentNotice, setSentNotice] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getShareLink(reportId)
      .then((l) => { if (alive) setLink(l); })
      .catch((err) => { if (alive) { setLink(null); setError(apiErrorMessage(err, t)); } });
    getReportEmailDraft(reportId)
      .then((d) => { if (alive) setDraft(d); })
      .catch(() => { if (alive) setDraft(null); });
    return () => { alive = false; };
  }, [reportId, t]);

  const onSent = (sent) => {
    setSending(false);
    if (sent.link) setLink(sent.link);
    setDraft((d) => (d ? { ...d, history: [sent, ...d.history] } : d));
    setSentNotice(noticeAfterSending(t, sent));
  };

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      setLink(await createShareLink(reportId));
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const revoke = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      await revokeShareLink(reportId);
      setLink(null);
      setConfirmRevoke(false);
    } catch (err) {
      setError(apiErrorMessage(err, t));
      setConfirmRevoke(false);
    } finally {
      setBusy(false);
    }
  }, [reportId, t]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
    } catch {
      // No clipboard API (http, old browser): select it for Ctrl+C.
      inputRef.current?.select();
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (value) => {
    try {
      return new Date(`${value}`.endsWith('Z') ? value : `${value}Z`).toLocaleString(i18n.language, {
        dateStyle: 'medium', timeStyle: 'short',
      });
    } catch {
      return value;
    }
  };

  return (
    <section className="share-panel" aria-labelledby="share-panel-title">
      <h4 id="share-panel-title" className="share-panel-title">{t('share.title')}</h4>
      <p className="share-panel-text">{t('share.explain')}</p>

      {error && <div className="ai-error" role="alert">{error}</div>}

      {link === undefined && <p className="share-panel-meta">{t('auth.loading')}</p>}

      {link === null && (
        <button type="button" className="btn btn-primary btn-sm" onClick={create} disabled={busy}>
          {t('share.create')}
        </button>
      )}

      {link && (
        <>
          <div className="share-link-row">
            <label htmlFor="share-link-url" className="visually-hidden">{t('share.linkLabel')}</label>
            <input
              id="share-link-url"
              ref={inputRef}
              className="form-control share-link-input"
              value={link.url}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={copy}>
              {copied ? t('share.copied') : t('share.copy')}
            </button>
            <a className="btn btn-secondary btn-sm" href={link.url} target="_blank" rel="noopener noreferrer">
              {t('share.open')}
            </a>
          </div>
          <p className="share-panel-meta" aria-live="polite">
            {link.view_count > 0
              ? t('share.views', { count: link.view_count, date: formatDate(link.last_viewed_at) })
              : t('share.notViewed')}
          </p>
          <button
            type="button"
            className="btn-step-delete"
            onClick={() => setConfirmRevoke(true)}
            disabled={busy}
          >
            {t('share.revoke')}
          </button>
        </>
      )}

      {draft && (
        <div className="send-row">
          <button
            type="button"
            className="btn btn-accent btn-sm"
            onClick={() => { setSentNotice(''); setSending(true); }}
            disabled={!draft.enabled}
          >
            {t('sendReport.open')}
          </button>
          {!draft.enabled && <span className="share-panel-meta">{t('sendReport.notConfigured')}</span>}
        </div>
      )}
      {sentNotice && <p className="send-notice" role="status">{sentNotice}</p>}
      {draft?.history?.length > 0 && (
        <ul className="send-history">
          {draft.history.slice(0, 3).map((h) => (
            <li key={`${h.sent_at}-${h.recipients.join()}`}>
              {t('sendReport.historyLine', {
                date: formatDate(h.sent_at),
                list: h.recipients.join(', '),
                parts: [
                  h.pdf_attached && t('sendReport.partPdf'),
                  h.link_included !== false && t('sendReport.partLink'),
                  h.invoice_attached && t('sendReport.partInvoice'),
                ].filter(Boolean).join(', '),
                interpolation: { escapeValue: false },
              })}
            </li>
          ))}
        </ul>
      )}

      {sending && draft && (
        <SendReportDialog
          reportId={reportId}
          draft={draft}
          onSent={onSent}
          onClose={() => setSending(false)}
        />
      )}

      {confirmRevoke && (
        <ConfirmDialog
          title={t('share.revokeTitle')}
          confirmLabel={t('share.revoke')}
          busy={busy}
          onConfirm={revoke}
          onCancel={() => setConfirmRevoke(false)}
        >
          <p>{t('share.revokeBody')}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
